import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { Button } from 'src/UIKit/components';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useDeleteCompanyCustomFieldApi,
  useDeleteContactCustomFieldApi,
  useGetCompanyCustomFieldsApi,
  useGetContactCustomFieldsApi,
  useUpdateCustomCompanyFieldApi,
} from 'src/api/customFields';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import {
  CompanyDefaultCustomField,
  ContactDefaultCustomField,
  CustomFieldFormData,
  FileLinkWithMimeType,
} from 'src/constants/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { CustomFieldSort } from 'src/api/customFields/types';
import { itemsPerPage } from './costants';
import CreateCustomFieldModal from './CreateCustomFieldModal/view';
import AttachFileUrlModal from 'src/components/AttachFileUrlModal/view';
import {
  Box,
  Stack,
  IconButton,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { BhiDialog } from 'src/UIKit';
import GridData from 'src/components/GridData/GridData';
import { TableHeader } from 'src/components/TableHeader/TableHeader';
import TooltipCell from 'src/components/TooltipCell';
import { GridPaginationModel, GridSortModel, GridRenderCellParams } from '@mui/x-data-grid';
import VcfDisplayFromUrl from 'src/components/VcfViewer/view';
import { clearMergeTagName, COMPANY_STANDARD_NAME_PLACEHOLDER } from 'src/helpers/customFieldMergeTag';
import { settingsTabPanelSx } from '../../helpers';

type CustomFieldProps = {
  showButtons: boolean;
  isCompanyFields: boolean;
  profileRole: string;
  refreshToken?: number;
};

const COMPANY_CONTACT_CARD_FIELD = 'company.standard.company_contact_card';
type MetadataField = CompanyDefaultCustomField | ContactDefaultCustomField;

function CustomFields({ showButtons, isCompanyFields, profileRole, refreshToken }: CustomFieldProps) {
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<CustomFieldSort>('name_asc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateCustomFieldModal, setCreateCustomFieldModal] = useState(false);
  const [showAttachFileModal, setAttachFileModal] = useState<boolean>(false);
  const selectedCompanyId: string | undefined = useSelector(getCurrentCompanyId);
  const [defaultFields, setDefaultFields] = useState<{
    count: number;
    items: MetadataField[];
  }>({ count: 0, items: [] });
  const [editableField, setEditableField] = useState<CustomFieldFormData | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const getCompanyFields = useGetCompanyCustomFieldsApi();
  const getContactFields = useGetContactCustomFieldsApi();
  const updateCompanyField = useUpdateCustomCompanyFieldApi();
  const deleteCompanyField = useDeleteCompanyCustomFieldApi();
  const deleteContactField = useDeleteContactCustomFieldApi();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewVcfUrl, setPreviewVcfUrl] = useState<string>('');
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handlePreviewClick = (vcfUrl: string) => {
    setPreviewVcfUrl(vcfUrl);
    setPreviewModalOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setPreviewVcfUrl('');
  };

  const showErrorDialog = (message: string) => {
    setErrorMessage(message);
    setErrorDialogOpen(true);
  };

  const closeErrorDialog = () => {
    setErrorDialogOpen(false);
    setErrorMessage('');
  };

  const isButtonsDisabled =
    (isCompanyFields && ['company_manager', 'company_user'].includes(profileRole)) ||
    (!isCompanyFields && !['bhi_admin', 'company_admin', 'company_manager'].includes(profileRole));

  const formatType = (type?: string): string => {
    if (type === 'string') {
      return 'text';
    }
    return type ? type : 'text';
  };

  const formatTableRows = (defaultField: MetadataField[]) => {
    return defaultField.map(
      ({ name, description, type, metadataId, value }: MetadataField) => {
        return {
          id: metadataId ?? name,
          fieldName: name,
          name: clearMergeTagName(name),
          description,
          type: formatType(type),
          actions: 'edit/delete',
          metadataId: metadataId,
          defaultValue: value,
        };
      },
    );
  };

  const refreshCustomFields = useCallback(() => {
    setEditableField(undefined);
    if (selectedCompanyId) {
      setIsLoading(true);
      const reqQuery: {
        companyId: string;
        sort: CustomFieldSort;
        query?: string;
      } = {
        companyId: selectedCompanyId,
        sort: sortField,
      };
      if (searchQuery) {
        reqQuery.query = searchQuery;
      }
      const request = isCompanyFields ? getCompanyFields(reqQuery) : getContactFields(reqQuery);
      request
        .then(data => {
          setDefaultFields({
            count: data.data.count,
            items: [...data.data.items],
          });
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    selectedCompanyId,
    sortField,
    searchQuery,
    isCompanyFields,
    getCompanyFields,
    getContactFields,
    refreshToken,
  ]);

  useEffect(() => {
    refreshCustomFields();
  }, [refreshCustomFields]);

  useEffect(() => {
    setPage(0);
  }, [sortField, searchQuery]);

  const handleContactFieldEdit = (name: string) => {
    if (defaultFields?.items) {
      const field = defaultFields.items.find(item => item.name === name);
      if (field) {
        setEditableField({
          id: field.name,
          metadataId: field.metadataId,
          type: field.type,
          name: clearMergeTagName(field.name),
          description: field.description,
          defaultValue: field.value,
        });

        setCreateCustomFieldModal(true);
      }
    }
  };

  const buildEditableCompanyField = (field: CompanyDefaultCustomField): CustomFieldFormData => {
    return {
      id: field.name,
      metadataId: field.metadataId,
      type: field.type,
      name: clearMergeTagName(field.name),
      description: field.description,
      defaultValue: field.value,
    };
  };

  const openCompanyFieldEditor = (name: string) => {
    if (defaultFields.items && selectedCompanyId) {
      const field = defaultFields.items.find(item => item.name === name) as CompanyDefaultCustomField | undefined;
      const isContactCard = field?.name === COMPANY_CONTACT_CARD_FIELD;
      if (field) {
        setEditableField(buildEditableCompanyField(field));
        if (isContactCard) {
          setAttachFileModal(true);
          setCreateCustomFieldModal(false);
        }
        if (!isContactCard) {
          setCreateCustomFieldModal(true);
          setAttachFileModal(false);
        }
      }
    }
  };

  const handleCompanyFieldEdit = (name: string) => {
    openCompanyFieldEditor(name);
  };

  const handleCustomFieldDelete = (id: string) => {
    if (!selectedCompanyId) {
      return;
    }

    const request = isCompanyFields
      ? deleteCompanyField(id, selectedCompanyId)
      : deleteContactField(id, selectedCompanyId);

    request
        .then(() => {
          refreshCustomFields();
        })
        .catch((error: { code?: number;  message?: string }) => {
          if (error?.code === 409) {
            showErrorDialog("This field is used by one or more Dynamic Groups. In order to delete this field, you must first remove it from all Dynamic Group rules that it is currently being used in.");
          } else {
            showErrorDialog('Something went wrong. Please try again.');
          }
          console.log(error);
        });
  };

  const isStandardField = (name: string) => {
    const prefix = isCompanyFields ? 'company.standard' : 'contact.standard';
    return name.startsWith(prefix);
  };

  const getValueDisplay = (value: string | null | undefined) => {
    if (value === null || value === undefined) {
      return { displayValue: '-', hasValue: false };
    }
    if (value.trim() === '' || value === 'null') {
      return { displayValue: '', hasValue: false };
    }
    return { displayValue: value, hasValue: true };
  };

  return (
    <>
      <AttachFileUrlModal
        title={editableField?.defaultValue ? 'Change Company Contact Card' : 'Upload Company Contact Card'}
        minHeight="290px"
        onlyUpload
        allowedFormats={'*.vcf'}
        fileAcceptTypes={{
          'text/vcard': ['.vcf'],
        }}
        link={editableField?.defaultValue || undefined}
        show={showAttachFileModal}
        setShowModal={setAttachFileModal}
        handleFileChange={(link: FileLinkWithMimeType) => {
          if (editableField) {
            setEditableField({ ...editableField, defaultValue: link.url });
            updateCompanyField(
              { metadataId: editableField.metadataId!, value: link.url },
              selectedCompanyId!,
            ).then(() => {
              refreshCustomFields();
            });
          }
        }}
      />
      <CreateCustomFieldModal
        isCompanyFields={isCompanyFields}
        show={showCreateCustomFieldModal}
        setShowModal={setCreateCustomFieldModal}
        customFieldData={editableField}
        refreshCustomFields={refreshCustomFields}
      />

      <Box sx={settingsTabPanelSx}>
        <GridData
          header={
            <TableHeader
              searchValue={searchQuery}
              onSearchChange={(_: ChangeEvent<HTMLInputElement>, query: string) => {
                setSearchQuery(query);
              }}
              actions={
                showButtons &&
                !isCompanyFields && (
                  <Button
                    disabled={isButtonsDisabled}
                    variant="contained"
                    onClick={() => {
                      setEditableField(undefined);
                      setCreateCustomFieldModal(true);
                    }}
                  >
                    Create Custom Field
                  </Button>
                )
              }
            />
          }
          isLoading={isLoading}
          rows={formatTableRows(
            defaultFields.items.slice(page * itemsPerPage, (page + 1) * itemsPerPage),
          )}
          columns={[
            {
              field: 'description',
              headerName: 'Name',
              renderCell: r => {
                return (
                  <Stack direction="row" alignItems="center" height="100%">
                    <TooltipCell value={r.formattedValue || ''} variant="body2" />
                  </Stack>
                );
              },
              minWidth: 150,
              sortable: true,
              editable: false,
              flex: 0.7,
            },
            {
              field: 'name',
              headerName: 'Merge Tag',
              renderCell: r => {
                return (
                  <Stack direction="row" alignItems="center" height="100%">
                    <TooltipCell value={r.formattedValue || ''} variant="body2" />
                  </Stack>
                );
              },
              minWidth: 120,
              sortable: true,
              editable: false,
              flex: 0.6,
            },
            ...(isCompanyFields
              ? [
                  {
                    field: 'defaultValue',
                    headerName: 'Value',
                    renderCell: (r: GridRenderCellParams) => {
                      const isContactCard = r.row.fieldName === COMPANY_CONTACT_CARD_FIELD;
                      const defaultValue = r.row.defaultValue;
                      const { displayValue, hasValue } = getValueDisplay(defaultValue);

                      return (
                        <Stack direction="row" alignItems="center" height="100%">
                          {isContactCard ? (
                            hasValue ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={e => {
                                  e.stopPropagation();
                                  handlePreviewClick(defaultValue || '');
                                }}
                              >
                                Preview
                              </Button>
                            ) : null
                          ) : (
                            <TooltipCell
                              value={displayValue}
                              variant="body2"
                              fontStyle={hasValue ? 'normal' : 'italic'}
                              color={hasValue ? 'text.primary' : 'text.secondary'}
                            />
                          )}
                        </Stack>
                      );
                    },
                    minWidth: 200,
                    sortable: false,
                    editable: false,
                    flex: 1.4,
                  },
                ]
              : []),
            ...(!isCompanyFields
              ? [
                  {
                    field: 'defaultValue',
                    headerName: 'Default Value',
                    renderCell: (r: GridRenderCellParams) => {
                      const { displayValue, hasValue } = getValueDisplay(r.row.defaultValue);
                      return (
                        <Stack direction="row" alignItems="center" height="100%">
                          <TooltipCell
                            value={displayValue}
                            variant="body2"
                            fontStyle={hasValue ? 'normal' : 'italic'}
                            color={hasValue ? 'text.primary' : 'text.secondary'}
                          />
                        </Stack>
                      );
                    },
                    minWidth: 150,
                    sortable: false,
                    editable: false,
                    flex: 0.8,
                  },
                  {
                    field: 'type',
                    headerName: 'Type',
                    minWidth: 200,
                    sortable: false,
                    editable: false,
                    flex: 1,
                  },
                ]
              : []),
          ]}
          renderAction={r => {
            if (r.fieldName === COMPANY_STANDARD_NAME_PLACEHOLDER) {
              return null;
            }

            return (
              <>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={e => {
                    e.stopPropagation();
                    isCompanyFields
                      ? handleCompanyFieldEdit(r.fieldName)
                      : handleContactFieldEdit(r.fieldName);
                  }}
                  disabled={isButtonsDisabled}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>

                {!isCompanyFields && !isStandardField(r.fieldName) && (
                  <IconButton
                    size="small"
                    disabled={isButtonsDisabled}
                    color="primary"
                    onClick={() =>
                      openDialog({
                        title: `Delete "${r.name}"`,
                        content: 'Are you sure you want to delete this custom field?',
                        onConfirm: () => handleCustomFieldDelete(r.metadataId!),
                      })
                    }
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                )}
              </>
            );
          }}
          sorting={{
            value: sortField,
            onChange: (s: GridSortModel) => {
              setSortField(`${s[0].field}_${s[0].sort}` as CustomFieldSort);
            },
          }}
          pagination={{
            page: page,
            pageSize: itemsPerPage,
            totalCount: defaultFields.count,
            onChange: (p: GridPaginationModel) => {
              setPage(p.page);
            },
          }}
        />
      </Box>

      <ConfirmationDialog />

      <BhiDialog
        isOpen={errorDialogOpen}
        onClose={closeErrorDialog}
        maxWidth="sm"
        title="Unable to Delete Custom Contact Field"
      >
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeErrorDialog}>OK</Button>
        </DialogActions>
      </BhiDialog>

      <BhiDialog
        open={previewModalOpen}
        onClose={handlePreviewClose}
        maxWidth="sm"
        fullWidth
        title="Company Contact Card Preview"
      >
        <DialogContent>
          {previewVcfUrl && <VcfDisplayFromUrl vcfUrl={previewVcfUrl} />}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handlePreviewClose}>
            Close
          </Button>
        </DialogActions>
      </BhiDialog>
    </>
  );
}

export default CustomFields;
